import { useCallback, useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';

export type TimeFormat = '12' | '24';

export interface TimePickerValue {
  /** 0–23 for both formats; the component handles AM/PM only for display. */
  hour: number;
  /** 0–59. */
  minute: number;
  /** 0–59. Omitted when `showSeconds` is false. */
  second?: number;
}

export interface TimePickerProps {
  value?: TimePickerValue;
  defaultValue?: TimePickerValue;
  onChange?: (value: TimePickerValue) => void;
  format?: TimeFormat;
  /** Show a seconds column. Default false. */
  showSeconds?: boolean;
  /** Step in minutes for the minute column. Default 1. For scheduling
   *  UIs, 5 or 15 are common. */
  minuteStep?: number;
  disabled?: boolean;
  invalid?: boolean;
  'aria-label'?: string;
  className?: string;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function pad(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n);
}

function to12h(hour24: number): { hour12: number; pm: boolean } {
  const pm = hour24 >= 12;
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, pm };
}

function from12h(hour12: number, pm: boolean): number {
  if (hour12 === 12) return pm ? 12 : 0;
  return hour12 + (pm ? 12 : 0);
}

/** Three-column time input: HH : MM [: SS] [AM/PM].
 *
 *  Each column is a role="spinbutton" that reads as "16, hour" to AT.
 *  Wheels (arrow up/down) wrap — 23+1 = 00, 00-1 = 23 — so users can
 *  count in either direction. Home/End clamp to extremes (00 / 59).
 *
 *  12-hour format is display-only: internally time is always stored
 *  as 0–23 hours. The AM/PM button toggles the PM bit without changing
 *  the hour column's visible 12-hour reading. */
export function TimePicker(props: TimePickerProps): ReactNode {
  const {
    value,
    defaultValue,
    onChange,
    format = '24',
    showSeconds = false,
    minuteStep = 1,
    disabled = false,
    invalid,
    className,
  } = props;

  const [time, setTime] = useControllableState<TimePickerValue>({
    defaultValue: defaultValue ?? { hour: 9, minute: 0 },
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });

  const { hour12, pm } = useMemo(() => to12h(time.hour), [time.hour]);

  const setHour = useCallback(
    (h: number) => {
      setTime({ ...time, hour: clamp(h, 0, 23) });
    },
    [time, setTime],
  );
  const setMinute = useCallback(
    (m: number) => {
      setTime({ ...time, minute: clamp(m, 0, 59) });
    },
    [time, setTime],
  );
  const setSecond = useCallback(
    (s: number) => {
      setTime({ ...time, second: clamp(s, 0, 59) });
    },
    [time, setTime],
  );

  /* Clamp minuteStep into [1, 60] — zero freezes the wheel, values >60
   * desynchronise from the mod-60 clock. Defensive for callers that
   * pass user input straight through. */
  const safeMinuteStep = Math.max(1, Math.min(60, Math.floor(minuteStep)));

  const bump = useCallback(
    (
      e: KeyboardEvent,
      {
        current,
        step,
        max,
        set,
      }: { current: number; step: number; max: number; set: (n: number) => void },
    ) => {
      /* Step normalised — zero would cause a no-op loop under long-hold
       * callers; negative values flip direction, accept abs. */
      const s = step === 0 ? 1 : Math.abs(step);
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        set((current + s) % (max + 1));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        set((current - s + (max + 1)) % (max + 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        set(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        set(max);
      }
    },
    [],
  );

  const togglePm = useCallback(() => {
    setHour(from12h(hour12, !pm));
  }, [hour12, pm, setHour]);

  const classes = [
    'vq-time-picker',
    `vq-time-picker--${format}`,
    disabled && 'vq-time-picker--disabled',
    invalid === true && 'vq-time-picker--invalid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const onHourChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const n = parseInt(e.target.value, 10);
    if (Number.isNaN(n)) return;
    if (format === '12') setHour(from12h(clamp(n, 1, 12), pm));
    else setHour(clamp(n, 0, 23));
  };

  return (
    <div
      className={classes}
      role="group"
      aria-label={props['aria-label'] ?? 'Time'}
      data-component="TimePicker"
      data-state={disabled ? 'disabled' : invalid === true ? 'invalid' : 'idle'}
    >
      <input
        type="text"
        inputMode="numeric"
        role="spinbutton"
        aria-label="Hour"
        aria-valuenow={format === '12' ? hour12 : time.hour}
        aria-valuemin={format === '12' ? 1 : 0}
        aria-valuemax={format === '12' ? 12 : 23}
        className="vq-time-picker__cell"
        value={format === '12' ? pad(hour12) : pad(time.hour)}
        onChange={onHourChange}
        onKeyDown={(e) => {
          bump(e, {
            current: time.hour,
            step: 1,
            max: 23,
            set: setHour,
          });
        }}
        disabled={disabled}
      />
      <span className="vq-time-picker__sep" aria-hidden="true">
        :
      </span>
      <input
        type="text"
        inputMode="numeric"
        role="spinbutton"
        aria-label="Minute"
        aria-valuenow={time.minute}
        aria-valuemin={0}
        aria-valuemax={59}
        className="vq-time-picker__cell"
        value={pad(time.minute)}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) setMinute(n);
        }}
        onKeyDown={(e) => {
          bump(e, {
            current: time.minute,
            step: safeMinuteStep,
            max: 59,
            set: setMinute,
          });
        }}
        disabled={disabled}
      />
      {showSeconds && (
        <>
          <span className="vq-time-picker__sep" aria-hidden="true">
            :
          </span>
          <input
            type="text"
            inputMode="numeric"
            role="spinbutton"
            aria-label="Second"
            aria-valuenow={time.second ?? 0}
            aria-valuemin={0}
            aria-valuemax={59}
            className="vq-time-picker__cell"
            value={pad(time.second ?? 0)}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) setSecond(n);
            }}
            onKeyDown={(e) => {
              bump(e, {
                current: time.second ?? 0,
                step: 1,
                max: 59,
                set: setSecond,
              });
            }}
            disabled={disabled}
          />
        </>
      )}
      {format === '12' && (
        <button
          type="button"
          className="vq-time-picker__period"
          onClick={togglePm}
          disabled={disabled}
          aria-label={pm ? 'PM' : 'AM'}
          aria-pressed={pm}
        >
          {pm ? 'PM' : 'AM'}
        </button>
      )}
    </div>
  );
}
